import React from "react";
import { useTranslation } from "react-i18next";
import { Select, DatePicker, Accordion } from "@foris/avocado-suite";
import { useProgrammingEditStore, setProgrammingFilters } from "@context/useProgrammingEditStore";
import { formatLocalDate, parseLocalDate } from "@utils/days";
import styles from "./programmingFilters.module.scss";

const DAY_KEYS = [
  { value: "Lunes", key: "programming-edit.filters.days.monday" },
  { value: "Martes", key: "programming-edit.filters.days.tuesday" },
  { value: "Miercoles", key: "programming-edit.filters.days.wednesday" },
  { value: "Jueves", key: "programming-edit.filters.days.thursday" },
  { value: "Viernes", key: "programming-edit.filters.days.friday" },
  { value: "Sabado", key: "programming-edit.filters.days.saturday" },
  { value: "Domingo", key: "programming-edit.filters.days.sunday" },
];

interface ComponentOption {
  value: string;
  label: string;
}

interface ProgrammingFiltersProps {
  componentOptions?: ComponentOption[];
}

const ProgrammingFilters: React.FC<ProgrammingFiltersProps> = ({ componentOptions = [] }) => {
  const { t } = useTranslation();
  const filters = useProgrammingEditStore(state => state.filters);

  const DAY_OPTIONS = DAY_KEYS.map(d => ({ value: d.value, label: t(d.key) }));

  const handleDateFrom = (date: Date) => {
    const nextFrom = date ? formatLocalDate(date) : null;
    const currentTo = useProgrammingEditStore.getState().filters.dateTo;
    const shouldClearTo = !!nextFrom && !!currentTo && currentTo < nextFrom;
    setProgrammingFilters({
      dateFrom: nextFrom,
      ...(shouldClearTo ? { dateTo: null } : {}),
    });
  };

  const handleDateTo = (date: Date) => {
    setProgrammingFilters({ dateTo: date ? formatLocalDate(date) : null });
  };

  const handleDayChange = (option: { value: string; label: string } | null) => {
    setProgrammingFilters({ dayOfWeek: option?.value ?? null });
  };

  const handleComponentChange = (option: { value: string; label: string } | null) => {
    setProgrammingFilters({ component: option?.value ?? null });
  };

  const selectedDay = DAY_OPTIONS.find(o => o.value === filters.dayOfWeek) ?? null;
  const selectedComponent = componentOptions.find(o => o.value === filters.component) ?? null;

  return (
    <Accordion
      className={styles.programmingFilters}
      title={t("programming-edit.filters.title")}
      opened
    >
      <div className={styles.programmingFilters_fields}>
        <div className={styles.programmingFilters_field}>
          <DatePicker
            label={t("programming-edit.filters.date-from")}
            placeholder={t("programming-edit.filters.select-date-placeholder")}
            selectedDate={filters.dateFrom ? parseLocalDate(filters.dateFrom) : undefined}
            onChange={handleDateFrom}
            isClearable={!!filters.dateFrom}
            maxDate={filters.dateTo ? parseLocalDate(filters.dateTo) : undefined}
          />
        </div>
        <div className={styles.programmingFilters_field}>
          <DatePicker
            label={t("programming-edit.filters.date-to")}
            placeholder={t("programming-edit.filters.select-date-placeholder")}
            selectedDate={filters.dateTo ? parseLocalDate(filters.dateTo) : undefined}
            onChange={handleDateTo}
            isClearable={!!filters.dateTo}
            minDate={filters.dateFrom ? parseLocalDate(filters.dateFrom) : undefined}
          />
        </div>
        <div className={styles.programmingFilters_field}>
          <Select
            label={t("programming-edit.filters.day-label")}
            placeholder={t("programming-edit.filters.day-placeholder")}
            options={DAY_OPTIONS}
            value={selectedDay}
            onChange={handleDayChange}
            isClearable
          />
        </div>
        <div className={styles.programmingFilters_field}>
          <Select
            label={t("programming-edit.filters.component-label")}
            placeholder={t("programming-edit.filters.component-placeholder")}
            options={componentOptions}
            value={selectedComponent}
            onChange={handleComponentChange}
            isClearable
          />
        </div>
      </div>
    </Accordion>
  );
};

export default ProgrammingFilters;
